import React from "react";
import PropTypes from "prop-types";
import IconButton from "@material-ui/core/IconButton";
import FirstPageIcon from "@material-ui/icons/FirstPage";
import KeyboardArrowLeft from "@material-ui/icons/KeyboardArrowLeft";
import KeyboardArrowRight from "@material-ui/icons/KeyboardArrowRight";
import LastPageIcon from "@material-ui/icons/LastPage";
import { useTheme as useMuiTheme } from "@material-ui/core/styles";

export const COMMON_PAGINATION_CLASS = "common-table-pagination";
export const COMMON_PAGINATION_MENU_PAPER_CLASS = "common-pagination-menu-paper";
export const COMMON_PAGINATION_MENU_LIST_CLASS = "common-pagination-menu-list";

/*
 * Shared rows-per-page select configuration.
 * We always use the MUI menu instead of the browser's native dropdown so
 * light/dark theme hover states stay consistent across every screen.
 */
export const commonPaginationSelectProps = {
  native: false,
  inputProps: {
    "aria-label": "rows per page",
  },
  MenuProps: {
    disableScrollLock: true,
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
    PaperProps: {
      className: COMMON_PAGINATION_MENU_PAPER_CLASS,
    },
    MenuListProps: {
      className: COMMON_PAGINATION_MENU_LIST_CLASS,
    },
  },
};

/*
 * Shared v4 inline style helper for legacy Material-UI tables.
 * This keeps text/icons readable in both themes while page layouts still
 * control alignment or width around the pagination container.
 */
export const getCommonPaginationStyle = (isDarkMode = false) => ({
  color: isDarkMode ? "#ffffff" : "inherit",
  borderBottom: "none",
  width: "fit-content",
});

/*
 * Shared v5 sx helper for screens using @mui/material TablePagination.
 * Nested selectors are kept here so page files do not need to repeat theme
 * overrides for labels, icons, or disabled actions.
 */
export const getCommonPaginationSx = (isDarkMode = false) => ({
  width: "fit-content",
  color: isDarkMode ? "#ffffff" : "inherit",
  borderBottom: "none",
  "& .MuiTablePagination-selectLabel": {
    color: isDarkMode ? "#ffffff" : "inherit",
  },
  "& .MuiTablePagination-displayedRows": {
    color: isDarkMode ? "#ffffff" : "inherit",
  },
  "& .MuiTablePagination-selectIcon": {
    color: "#036dda",
  },
  "& .MuiIconButton-root": {
    color: "#036dda",
  },
  "& .MuiIconButton-root.Mui-disabled": {
    color: isDarkMode ? "rgba(3, 109, 218, 0.3)" : "rgba(3, 109, 218, 0.35)",
  },
});

function CommonTablePaginationActions(props) {
  const classes = props.classes || {};
  const theme = useMuiTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event || null, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event || null, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event || null, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event || null, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <div className={classes.root}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </div>
  );
}

CommonTablePaginationActions.propTypes = {
  classes: PropTypes.object,
  count: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
};

export default CommonTablePaginationActions;
